import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { PANEL_HEADER, PANEL_TITLE } from './panelChrome';
import { IconButton, buttonClass } from '../../components/ui/Button';
import Chip from '../../components/ui/Chip';
import SegTrack from '../../components/ui/SegTrack';
import UiTooltip from '../../components/ui/Tooltip';
import { MAP_TIP, MapTipStyle } from '../../components/ui/PopoverMapTip';
import Appear from '../../components/ui/Appear';

const FlightPath3D = lazy(() => import('./FlightPath3D'));

interface FlightPoint {
  lat: number;
  lon: number;
  alt: number;
  timestamp_s: number;
}

interface DroneTrack {
  id: string;
  label: string;
  color: string;
  points: FlightPoint[];
  visible: boolean;
}

interface FlightPathMapProps {
  tracks: DroneTrack[];
  onToggleTrack?: (id: string) => void;
  onTimeChange?: (timestamp_s: number) => void;
  onTimeWindowSelect?: (start_s: number, end_s?: number) => void;
  externalFocusTime?: number | null;
  height?: number;
}

const DRONE_COLORS = [
  '#ffcc33', '#3b82f6', '#ef4444', '#34d399', '#f97316',
  '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b', '#6366f1',
];

/* ── Map auto-fit ── */
const FitBounds: React.FC<{ tracks: DroneTrack[] }> = ({ tracks }) => {
  const map = useMap();
  useEffect(() => {
    const allPoints: [number, number][] = [];
    tracks.forEach(t => {
      if (t.visible) t.points.forEach(p => allPoints.push([p.lat, p.lon]));
    });
    if (allPoints.length > 1) {
      map.fitBounds(allPoints, { padding: [30, 30] });
    } else if (allPoints.length === 1) {
      map.setView(allPoints[0], 17);
    }
  }, [tracks, map]);
  return null;
};

/* ── Time scrubber ── */
const TimeScrubber: React.FC<{
  minTime: number;
  maxTime: number;
  currentTime: number;
  selectionStart: number | null;
  selectionEnd: number | null;
  onTimeChange: (t: number) => void;
  onSelectionChange: (start: number | null, end: number | null) => void;
  onAnalyzeWindow?: (start_s: number, end_s?: number) => void;
  isPlaying: boolean;
  onPlayToggle: () => void;
}> = ({ minTime, maxTime, currentTime, selectionStart, selectionEnd, onTimeChange, onSelectionChange, onAnalyzeWindow, isPlaying, onPlayToggle }) => {
  const barRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const selecting = useRef(false);

  const tToX = (t: number) => ((t - minTime) / (maxTime - minTime)) * 100;

  const xToT = useCallback((clientX: number) => {
    if (!barRef.current) return minTime;
    const rect = barRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return minTime + pct * (maxTime - minTime);
  }, [minTime, maxTime]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.shiftKey) {
      selecting.current = true;
      onSelectionChange(xToT(e.clientX), null);
    } else {
      dragging.current = true;
      onTimeChange(xToT(e.clientX));
      // Don't clear selection — user uses IN/OUT buttons and CLEAR explicitly
    }
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (dragging.current) onTimeChange(xToT(e.clientX));
    if (selecting.current && selectionStart !== null) onSelectionChange(selectionStart, xToT(e.clientX));
  };

  const handlePointerUp = () => {
    dragging.current = false;
    selecting.current = false;
  };

  // Keyboard: arrows move the playhead one second, Shift-arrows ten, Home and End jump.
  // With Shift held and no window started, [ and ] set the window's start and end.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 10 : 1;
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = Math.min(maxTime, currentTime + step);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = Math.max(minTime, currentTime - step);
    else if (e.key === 'Home') next = minTime;
    else if (e.key === 'End') next = maxTime;
    else if (e.key === '[') { onSelectionChange(currentTime, selectionEnd); e.preventDefault(); return; }
    else if (e.key === ']') { onSelectionChange(selectionStart ?? minTime, currentTime); e.preventDefault(); return; }
    else if (e.key === ' ') { onPlayToggle(); e.preventDefault(); return; }
    if (next !== null) { onTimeChange(next); e.preventDefault(); }
  };

  const hasWindow = selectionStart !== null && selectionEnd !== null;
  const hasIn = selectionStart !== null;
  const windowStart = hasWindow ? Math.min(selectionStart!, selectionEnd!) : (selectionStart ?? 0);
  const windowEnd = hasWindow ? Math.max(selectionStart!, selectionEnd!) : 0;
  // Gotcha: the highlight fades out after hasWindow turns false, so it keeps its last span.
  const lastSpan = useRef({ start: 0, end: 0 });
  if (hasWindow) lastSpan.current = { start: windowStart, end: windowEnd };
  const shownStart = hasWindow ? windowStart : lastSpan.current.start;
  const shownEnd = hasWindow ? windowEnd : lastSpan.current.end;

  const startLabel = selectionStart !== null ? formatTime(selectionStart) : null;
  const endLabel = selectionEnd !== null ? formatTime(selectionEnd) : null;

  return (
    <div className="bg-surface-container px-3 pb-2.5 pt-1.5">
      {/* Controls row */}
      <div className="mb-1.5 flex min-w-0 items-center gap-1.5 overflow-x-auto">
        <UiTooltip label={isPlaying ? 'Pause the replay' : 'Replay the flight from the playhead'}>
          <IconButton
            label={isPlaying ? 'Pause' : 'Play'}
            onClick={onPlayToggle}
          >
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
              {isPlaying
                ? <path fill="currentColor" d="M4 3h3v10H4zM9 3h3v10H9z" />
                : <path fill="currentColor" d="M4.5 2.5v11L13 8z" />}
            </svg>
          </IconButton>
        </UiTooltip>
        <span className="min-w-[3.5rem] font-mono text-xs tabular-nums text-on-surface">
          T+{formatTime(currentTime)}
        </span>

        <span aria-hidden="true" className="mx-0.5 h-3.5 w-px shrink-0 bg-outline-variant" />

        {/* Set In / Set Out */}
        <UiTooltip label="Mark the playhead as the start of the window">
          <button
            type="button"
            onClick={() => onSelectionChange(currentTime, selectionEnd)}
            className={buttonClass('quiet', 'sm')}
          >
            {startLabel ? <>Start <span className="font-mono tabular-nums">{startLabel}</span></> : 'Set start'}
          </button>
        </UiTooltip>
        <UiTooltip label={selectionStart === null ? 'Set a start first' : 'Mark the playhead as the end of the window'}>
          <button
            type="button"
            onClick={() => onSelectionChange(selectionStart, currentTime)}
            disabled={selectionStart === null}
            className={buttonClass('quiet', 'sm')}
          >
            {endLabel ? <>End <span className="font-mono tabular-nums">{endLabel}</span></> : 'Set end'}
          </button>
        </UiTooltip>

        <Appear show={selectionStart !== null}>
          {selectionStart !== null && (
            <UiTooltip
              label={hasWindow
                ? `Point the analyst at T+${formatTime(windowStart)} to T+${formatTime(windowEnd)}`
                : `Point the analyst at T+${formatTime(selectionStart!)} to the end of the log`}
            >
              <button
                type="button"
                onClick={() => onAnalyzeWindow?.(windowStart, hasWindow ? windowEnd : undefined)}
                className={buttonClass('filled', 'sm')}
              >
                {hasWindow ? 'Use window' : 'Use start to end'}
              </button>
            </UiTooltip>
          )}
        </Appear>
        <Appear show={selectionStart !== null}>
          <button
            type="button"
            onClick={() => onSelectionChange(null, null)}
            className={buttonClass('quiet', 'sm')}
          >
            Clear window
          </button>
        </Appear>

        <span className="ml-auto shrink-0 font-mono text-xs tabular-nums text-on-surface-variant">
          {formatTime(minTime)}{'\u2013'}{formatTime(maxTime)}
        </span>
      </div>

      {/* Scrubber bar */}
      <div
        ref={barRef}
        role="slider"
        tabIndex={0}
        aria-label="Flight time"
        aria-valuemin={Math.round(minTime)}
        aria-valuemax={Math.round(maxTime)}
        aria-valuenow={Math.round(currentTime)}
        aria-valuetext={`${formatTime(currentTime)} of ${formatTime(maxTime)}`}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className="relative h-5 cursor-pointer touch-none rounded bg-surface-container-highest focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        {/* Selection highlight */}
        <div
          className="absolute inset-y-0"
          style={{
            left: `${tToX(shownStart)}%`,
            width: `${tToX(shownEnd) - tToX(shownStart)}%`,
          }}
        >
          <Appear as="div" show={hasWindow} className="h-full border-x-2 border-primary/50 bg-primary/15" />
        </div>
        {/* Playhead */}
        <div
          className="absolute -inset-y-0.5 w-0.5 -translate-x-px rounded-sm bg-primary"
          style={{ left: `${tToX(currentTime)}%` }}
        />
      </div>
    </div>
  );
};

/* ── Main Component ── */
const FlightPathMap: React.FC<FlightPathMapProps> = ({
  tracks, onToggleTrack, onTimeChange, onTimeWindowSelect, externalFocusTime, height = 350,
}) => {
  const [viewMode, setViewMode] = useState<'3d' | '2d'>('3d');
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectionStart, setSelectionStart] = useState<number | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<number | null>(null);
  const playRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Compute time bounds across all visible tracks
  const { minTime, maxTime } = useMemo(() => {
    let min = Infinity, max = -Infinity;
    tracks.forEach(t => {
      if (!t.visible || t.points.length === 0) return;
      min = Math.min(min, t.points[0].timestamp_s);
      max = Math.max(max, t.points[t.points.length - 1].timestamp_s);
    });
    return { minTime: min === Infinity ? 0 : min, maxTime: max === -Infinity ? 1 : max };
  }, [tracks]);

  // Initialize time
  useEffect(() => {
    if (currentTime === 0 && minTime > 0) setCurrentTime(minTime);
  }, [minTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync external focus time (from chart hover) → internal currentTime
  useEffect(() => {
    if (externalFocusTime != null && !isPlaying) {
      setCurrentTime(externalFocusTime);
    }
  }, [externalFocusTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Playback
  useEffect(() => {
    if (isPlaying) {
      playRef.current = setInterval(() => {
        setCurrentTime(prev => {
          const next = prev + 0.5;
          if (next > maxTime) { setIsPlaying(false); return minTime; }
          return next;
        });
      }, 100);
      return () => { if (playRef.current) clearInterval(playRef.current); };
    }
  }, [isPlaying, maxTime, minTime]);

  const handleTimeChange = useCallback((t: number) => {
    setCurrentTime(t);
    setIsPlaying(false);
    onTimeChange?.(t);
  }, [onTimeChange]);

  const handleSelectionChange = useCallback((start: number | null, end: number | null) => {
    setSelectionStart(start);
    setSelectionEnd(end);
    // Don't auto-trigger analysis — just store the window.
    // User clicks ANALYZE WINDOW button or sends a chat message to use it.
  }, []);

  // Find current position for each track
  const currentPositions = useMemo(() => {
    return tracks.map(track => {
      if (!track.visible || track.points.length === 0) return null;
      // Find nearest point to currentTime
      let closest = track.points[0];
      for (const p of track.points) {
        if (Math.abs(p.timestamp_s - currentTime) < Math.abs(closest.timestamp_s - currentTime)) {
          closest = p;
        }
      }
      return { lat: closest.lat, lon: closest.lon, alt: closest.alt, color: track.color, label: track.label };
    }).filter(Boolean);
  }, [tracks, currentTime]);

  if (tracks.length === 0 || tracks.every(t => t.points.length === 0)) return null;

  // Center point for initial map view
  const firstTrack = tracks.find(t => t.visible && t.points.length > 0);
  const center: [number, number] = firstTrack
    ? [firstTrack.points[Math.floor(firstTrack.points.length / 2)].lat,
       firstTrack.points[Math.floor(firstTrack.points.length / 2)].lon]
    : [0, 0];

  return (
    <section className="flex h-full min-w-0 flex-col overflow-hidden bg-surface-container-low">
      <div className={PANEL_HEADER}>
        <h3 className={PANEL_TITLE}>Flight path</h3>
        <div className="flex min-w-0 items-center gap-2">
          {/* 2D/3D toggle */}
          <SegTrack
            label="View"
            size="sm"
            className="shrink-0"
            value={viewMode}
            onChange={(mode) => setViewMode(mode as typeof viewMode)}
            options={[{ key: '3d', label: '3D' }, { key: '2d', label: '2D' }]}
          />
          <div className="flex min-w-0 items-center gap-1 overflow-x-auto">
            {tracks.map(track => (
              <Chip
                key={track.id}
                pressed={track.visible}
                onClick={() => onToggleTrack?.(track.id)}
                className="shrink-0 font-mono"
                leading={(
                  <span
                    aria-hidden="true"
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={track.visible
                      ? { background: track.color }
                      : { boxShadow: `inset 0 0 0 1.5px ${track.color}` }}
                  />
                )}
              >
                {track.label}
              </Chip>
            ))}
          </div>
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
        <MapTipStyle />
        {viewMode === '3d' ? (
          <Suspense
            fallback={(
              <div className="flex h-full items-center justify-center bg-surface text-sm text-on-surface-variant">
                Building the 3D view
              </div>
            )}
          >
            <FlightPath3D tracks={tracks} currentTime={currentTime} />
          </Suspense>
        ) : (
        <MapContainer
          center={center}
          zoom={17}
          style={{ height: '100%', width: '100%', background: 'var(--surface)' }}
          zoomControl={false}
          attributionControl={false}
        >
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
          <FitBounds tracks={tracks} />

          {/* Flight paths */}
          {tracks.filter(t => t.visible).map(track => (
            <Polyline
              key={track.id}
              positions={track.points.map(p => [p.lat, p.lon] as [number, number])}
              pathOptions={{
                color: track.color,
                weight: 2.5,
                opacity: 0.8,
              }}
            />
          ))}

          {/* Current position markers */}
          {currentPositions.map((pos: any, i: number) => {
            const icon = L.divIcon({
              className: '',
              html: `<div style="width:12px;height:12px;border-radius:50%;background:${pos.color};border:2px solid var(--on-surface);box-shadow:0 0 6px ${pos.color}"></div>`,
              iconSize: [12, 12],
              iconAnchor: [6, 6],
            });
            const MarkerAny = Marker as any;
            const TooltipAny = Tooltip as any;
            return (
              <MarkerAny key={i} position={[pos.lat, pos.lon]} icon={icon}>
                <TooltipAny direction="top" offset={[0, -10]} className={MAP_TIP}>
                  <span className="font-mono text-xs tabular-nums">
                    {pos.label}, {pos.alt.toFixed(1)} m above takeoff
                  </span>
                </TooltipAny>
              </MarkerAny>
            );
          })}
        </MapContainer>
        )}
      </div>

      {/* Time scrubber */}
      <TimeScrubber
        minTime={minTime}
        maxTime={maxTime}
        currentTime={currentTime}
        selectionStart={selectionStart}
        selectionEnd={selectionEnd}
        onTimeChange={handleTimeChange}
        onSelectionChange={handleSelectionChange}
        onAnalyzeWindow={onTimeWindowSelect}
        isPlaying={isPlaying}
        onPlayToggle={() => setIsPlaying(p => !p)}
      />
    </section>
  );
};

export default FlightPathMap;
export { DRONE_COLORS };
export type { DroneTrack, FlightPoint };
